/* ==========================================================================
   Faja Fina · product.js
   Galería, zoom, selector de packs con talla/color por unidad, selector de
   variantes clásico, formulario de compra y barra fija opcional.
   ========================================================================== */
(() => {
  const FF = (window.FF = window.FF || {});
  const S = () => FF.strings || {};

  const sameOptions = (variant, options) => variant.options.every((value, index) => value === options[index]);

  /* ---------- Galería ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.thumbs = [...this.querySelectorAll('[data-thumb-index]')];
      this.thumbsTrack = this.querySelector('[data-gallery-thumbs]');

      const prev = this.querySelector('[data-gallery-prev]');
      const next = this.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.goTo(this.index - 1));
      if (next) next.addEventListener('click', () => this.goTo(this.index + 1));

      const thumbsPrev = this.querySelector('[data-thumbs-prev]');
      const thumbsNext = this.querySelector('[data-thumbs-next]');
      if (thumbsPrev) thumbsPrev.addEventListener('click', () => this.thumbsTrack.scrollBy({ left: -this.thumbsTrack.clientWidth * 0.75, behavior: 'smooth' }));
      if (thumbsNext) thumbsNext.addEventListener('click', () => this.thumbsTrack.scrollBy({ left: this.thumbsTrack.clientWidth * 0.75, behavior: 'smooth' }));

      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.thumbIndex))));

      this.track.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-lightbox-open]');
        if (trigger) this.openLightbox(Number(trigger.dataset.lightboxOpen));
      });
      this.track.addEventListener('scroll', FF.debounce(() => this.syncFromScroll(), 60), { passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.goTo(this.index + 1);
        if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
      });

      const initial = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.index = initial > -1 ? initial : 0;
      if (this.index > 0) requestAnimationFrame(() => this.goTo(this.index, false));
    }

    slideWidth() {
      const first = this.slides[0];
      if (!first) return 1;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return first.offsetWidth + gap;
    }

    goTo(index, smooth = true) {
      const count = this.slides.length;
      if (!count) return;
      const target = (index + count) % count;
      this.track.scrollTo({ left: target * this.slideWidth(), behavior: smooth ? 'smooth' : 'auto' });
      this.setActive(target);
    }

    goToMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index > -1 && index !== this.index) this.goTo(index);
    }

    syncFromScroll() {
      const index = Math.round(this.track.scrollLeft / this.slideWidth());
      if (index !== this.index) this.setActive(index);
    }

    setActive(index) {
      this.index = index;
      this.slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        const video = slide.querySelector('video');
        if (video && !active) video.pause();
      });
      this.thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
          this.centerThumb(thumb);
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    centerThumb(thumb) {
      if (!this.thumbsTrack) return;
      const item = thumb.parentElement;
      const first = this.thumbs[0].parentElement;
      const left = item.offsetLeft - first.offsetLeft - (this.thumbsTrack.clientWidth - item.offsetWidth) / 2;
      this.thumbsTrack.scrollTo({ left, behavior: 'smooth' });
    }

    openLightbox(index) {
      const lightbox = document.getElementById(`Lightbox-${this.dataset.section}`);
      if (lightbox && typeof lightbox.open === 'function') lightbox.open(index);
    }
  }

  /* ---------- Zoom a pantalla completa ---------- */
  class GalleryLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[role="dialog"]');
      this.querySelector('[data-lightbox-close]').addEventListener('click', () => this.close());
      this.closeHandler = () => this.close();
    }

    open(index) {
      this.opener = document.activeElement;
      this.classList.add('is-open');
      FF.lockScroll();
      FF.trapFocus(this, this.querySelector('[data-lightbox-close]'));
      FF.pushEscape(this.closeHandler);
      const item = this.querySelector(`[data-lightbox-index="${index}"]`);
      requestAnimationFrame(() => {
        this.scrollTop = item ? item.offsetTop : 0;
      });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      FF.unlockScroll();
      FF.popEscape(this.closeHandler);
      FF.removeTrapFocus(this.opener);
    }
  }

  /* ---------- Packs: 2 / 4 / 6 unidades con talla y color por unidad ---------- */
  class OfferSelector extends HTMLElement {
    init(product) {
      this.product = product;
      this.offers = [...this.querySelectorAll('[data-offer]')];
      this.template = this.querySelector('[data-unit-template]');
      this.unitsLabel = this.querySelector('[data-units-label]');
      this.error = this.querySelector('[data-offer-error]');
      this.units = [];

      const start =
        product.variants.find((v) => v.id === product.selected_variant_id && v.available) ||
        product.variants.find((v) => v.available) ||
        product.variants[0];
      this.defaultOptions = start ? [...start.options] : [];

      this.offers.forEach((offer) => {
        const radio = offer.querySelector('.offer__radio');
        radio.addEventListener('change', () => {
          if (radio.checked) this.select(offer);
        });
        offer.addEventListener('click', (event) => {
          if (event.target.closest('select, .offer__units')) return;
          if (this.current !== offer) {
            radio.checked = true;
            this.select(offer);
          }
        });
      });

      const selected = this.offers.find((offer) => offer.classList.contains('is-selected')) || this.offers[0];
      if (selected) this.select(selected, true);
    }

    get quantity() {
      return Number((this.current && this.current.dataset.qty) || 1);
    }

    findVariant(options) {
      return this.product.variants.find((variant) => sameOptions(variant, options));
    }

    select(offer, initial = false) {
      if (this.current === offer && !initial) return;
      this.current = offer;
      this.offers.forEach((item) => {
        const active = item === offer;
        item.classList.toggle('is-selected', active);
        item.querySelector('.offer__radio').checked = active;
        const units = item.querySelector('[data-offer-units]');
        if (units && !active) {
          units.hidden = true;
          units.innerHTML = '';
        }
      });
      this.renderUnits();
      this.emit(null);
    }

    renderUnits() {
      const container = this.current.querySelector('[data-offer-units]');
      if (!container) return;
      if (!this.template || this.product.has_only_default_variant) {
        container.hidden = true;
        return;
      }
      container.innerHTML = '';
      if (this.unitsLabel) {
        const label = this.unitsLabel.cloneNode(true);
        label.hidden = false;
        label.removeAttribute('data-units-label');
        container.appendChild(label);
      }
      for (let i = 0; i < this.quantity; i += 1) {
        if (!this.units[i]) this.units[i] = [...(this.units[i - 1] || this.defaultOptions)];
        const row = this.template.content.firstElementChild.cloneNode(true);
        row.dataset.unit = i;
        row.querySelector('[data-unit-index]').textContent = `#${i + 1}`;
        row.querySelectorAll('select[data-unit-option]').forEach((select) => {
          const position = Number(select.dataset.unitOption);
          select.value = this.units[i][position];
          select.setAttribute('aria-label', `${select.getAttribute('aria-label')} · ${S().unitLabel || 'Unidad'} ${i + 1}`);
          select.addEventListener('change', () => {
            this.units[i][position] = select.value;
            this.refreshRow(row, i);
            this.emit(this.findVariant(this.units[i]));
          });
        });
        container.appendChild(row);
        this.refreshRow(row, i);
      }
      container.hidden = false;
    }

    refreshRow(row, index) {
      const options = this.units[index];
      row.querySelectorAll('select[data-unit-option]').forEach((select) => {
        const position = Number(select.dataset.unitOption);
        if (select.dataset.color === 'true') {
          const swatch = select.parentElement.querySelector('[data-swatch]');
          if (swatch) swatch.style.setProperty('--swatch', FF.swatchColor(select.value) || '#e5dfe2');
        }
        [...select.options].forEach((option) => {
          const test = [...options];
          test[position] = option.value;
          const variant = this.findVariant(test);
          option.disabled = !variant;
          option.textContent = variant && !variant.available ? `${option.value} · ${(S().soldOut || 'Agotado').toLowerCase()}` : option.value;
        });
      });
      const variant = this.findVariant(options);
      row.classList.toggle('is-unavailable', !variant || !variant.available);
    }

    unitVariants() {
      if (this.product.has_only_default_variant) return Array(this.quantity).fill(this.product.variants[0]);
      return Array.from({ length: this.quantity }, (_, i) => this.findVariant(this.units[i] || this.defaultOptions));
    }

    getItems() {
      const counts = new Map();
      this.unitVariants().forEach((variant) => {
        if (variant) counts.set(variant.id, (counts.get(variant.id) || 0) + 1);
      });
      return [...counts.entries()].map(([id, quantity]) => ({ id, quantity }));
    }

    emit(focusVariant) {
      const variants = this.unitVariants();
      const valid = variants.every((variant) => variant && variant.available);
      if (this.error) {
        this.error.hidden = valid;
        if (!valid) this.error.textContent = S().unavailableUnits || `${S().unavailable || 'No disponible'}: cambia la talla o el color marcado en rojo.`;
      }
      this.dispatchEvent(
        new CustomEvent('offer:change', {
          bubbles: true,
          detail: {
            valid,
            items: this.getItems(),
            primaryVariant: variants[0],
            focusVariant: focusVariant || null,
            tier: {
              index: Number(this.current.dataset.tier),
              quantity: this.quantity,
              price: Number(this.current.dataset.price),
              compare: Number(this.current.dataset.compare),
            },
          },
        })
      );
    }
  }

  /* ---------- Selector de variantes clásico ---------- */
  class VariantPicker extends HTMLElement {
    init(product) {
      this.product = product;
      this.querySelectorAll('[data-swatch-name]').forEach((el) => el.style.setProperty('--swatch', FF.swatchColor(el.dataset.swatchName) || '#e5dfe2'));
      this.addEventListener('change', () => this.update(false));
      this.update(true);
    }

    selected() {
      return [...this.querySelectorAll('fieldset')].map((fieldset) => {
        const input = fieldset.querySelector('input:checked');
        return input ? input.value : null;
      });
    }

    update(initial) {
      const options = this.selected();
      this.querySelectorAll('fieldset').forEach((fieldset, i) => {
        const label = fieldset.querySelector('[data-option-value]');
        if (label) label.textContent = options[i] || '';
      });
      this.querySelectorAll('input[data-option-input]').forEach((input) => {
        const position = Number(input.dataset.optionInput);
        const test = [...options];
        test[position] = input.value;
        const variant = this.product.variants.find((v) => sameOptions(v, test));
        input.disabled = !variant || !variant.available;
      });
      const variant = this.product.variants.find((v) => sameOptions(v, options));
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant, initial } }));
    }
  }

  /* ---------- Formulario de compra ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-atc]');
      this.errorEl = this.querySelector('[data-form-error]');
      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    setLoading(loading) {
      if (!this.button) return;
      this.button.classList.toggle('is-loading', loading);
      this.button.setAttribute('aria-busy', String(loading));
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = !message;
    }

    async onSubmit(event) {
      if (!FF.cart) return; // sin JS de carrito: envío normal del formulario
      event.preventDefault();
      if (this.button.disabled || this.button.classList.contains('is-loading')) return;
      const info = this.closest('product-info');
      const items = info ? info.getItems() : [{ id: Number(new FormData(this.form).get('id')), quantity: 1 }];
      if (!items.length) return;
      this.showError('');
      this.setLoading(true);
      try {
        await FF.cart.add(items, this.button);
      } catch (error) {
        this.showError(error.message || S().error);
      } finally {
        this.setLoading(false);
      }
    }
  }

  /* ---------- Barra fija de compra (opcional) ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const section = document.getElementById(`Product-${this.dataset.section}`);
      if (!section) return;
      this.hidden = false;
      this.priceEl = this.querySelector('[data-sticky-price]');
      const offers = section.querySelector('offer-selector');
      const atc = section.querySelector('[data-atc]');
      this.querySelector('[data-sticky-buy]').addEventListener('click', () => {
        if (offers && FF.scrollToTarget(`#${offers.id}`)) return;
        if (atc) atc.click();
      });
      const target = atc || offers;
      if (!target || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', show);
        document.body.classList.toggle('has-sticky-atc', show);
      });
      this.observer.observe(target);
    }

    update(price) {
      if (this.priceEl && Number.isFinite(price)) this.priceEl.innerHTML = FF.formatMoney(price);
    }
  }

  /* ---------- Controlador de la ficha ---------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      if (!json) return;
      try {
        this.product = JSON.parse(json.textContent);
      } catch (error) {
        return;
      }
      this.sectionId = this.dataset.section;
      this.gallery = this.querySelector('product-gallery');
      this.offers = this.querySelector('offer-selector');
      this.picker = this.querySelector('variant-picker');
      this.variantInput = this.querySelector('[data-variant-id]');
      this.inventory = this.querySelector('[data-inventory]');
      this.atc = this.querySelector('[data-atc]');
      this.atcLabel = this.querySelector('[data-atc-label]');
      this.atcText = this.atcLabel ? this.atcLabel.textContent.trim() : S().addToCart;

      this.addEventListener('offer:change', (event) => this.onOfferChange(event.detail));
      this.addEventListener('variant:change', (event) => this.onVariantChange(event.detail));

      if (this.offers && typeof this.offers.init === 'function') this.offers.init(this.product);
      else if (this.picker && typeof this.picker.init === 'function') this.picker.init(this.product);
    }

    get stickyBar() {
      return document.querySelector(`sticky-atc[data-section="${this.sectionId}"]`);
    }

    getItems() {
      if (this.offers && typeof this.offers.getItems === 'function') return this.offers.getItems();
      const qtyInput = this.querySelector('[data-quantity-input]');
      const quantity = Math.max(1, Number(qtyInput ? qtyInput.value : 1));
      return [{ id: Number(this.variantInput.value), quantity }];
    }

    setAtc(enabled, label) {
      if (!this.atc) return;
      this.atc.disabled = !enabled;
      if (this.atcLabel) this.atcLabel.textContent = label || this.atcText;
    }

    showMedia(variant) {
      if (!variant || !variant.featured_media_id || !this.gallery) return;
      if (this.lastMediaId === variant.featured_media_id) return;
      this.lastMediaId = variant.featured_media_id;
      this.gallery.goToMedia(variant.featured_media_id);
    }

    updateInventory(variant) {
      if (!this.inventory || !variant) return;
      const threshold = Number(this.inventory.dataset.threshold || 10);
      const stock = variant.inventory_quantity;
      const show = variant.inventory_management === 'shopify' && stock !== null && stock > 0 && stock <= threshold;
      this.inventory.hidden = !show;
      const text = this.inventory.querySelector('[data-inventory-text]');
      if (show && text) text.textContent = (this.inventory.dataset.template || '').replace('[count]', stock);
    }

    onOfferChange({ valid, primaryVariant, focusVariant, tier }) {
      if (primaryVariant && this.variantInput) this.variantInput.value = primaryVariant.id;
      this.updateInventory(focusVariant || primaryVariant);
      if (focusVariant) this.showMedia(focusVariant);
      this.setAtc(valid, valid ? this.atcText : S().unavailable);
      const bar = this.stickyBar;
      if (bar && typeof bar.update === 'function') bar.update(tier.price);
    }

    onVariantChange({ variant, initial }) {
      if (!variant) {
        this.setAtc(false, S().unavailable);
        return;
      }
      if (this.variantInput) this.variantInput.value = variant.id;
      this.setAtc(variant.available, variant.available ? this.atcText : S().soldOut);
      this.updateInventory(variant);
      if (!initial) this.showMedia(variant);

      const priceBlock = this.querySelector('[data-price-block] [data-price]');
      if (priceBlock) {
        const sale = variant.compare_at_price > variant.price;
        priceBlock.classList.toggle('price--sale', sale);
        priceBlock.innerHTML =
          `<span class="price__current">${FF.formatMoney(variant.price)}</span>` +
          (sale ? `<s class="price__compare">${FF.formatMoney(variant.compare_at_price)}</s>` : '');
      }
      const bar = this.stickyBar;
      if (bar && typeof bar.update === 'function') bar.update(variant.price);

      if (!initial && this.dataset.updateUrl === 'true' && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }
  }

  /* El orden importa: los hijos se definen antes que el controlador. */
  customElements.define('product-gallery', ProductGallery);
  customElements.define('gallery-lightbox', GalleryLightbox);
  customElements.define('offer-selector', OfferSelector);
  customElements.define('variant-picker', VariantPicker);
  customElements.define('product-form', ProductForm);
  customElements.define('sticky-atc', StickyAtc);
  customElements.define('product-info', ProductInfo);
})();
